const US_CORE_RACE_URL = 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-race'
const US_CORE_ETHNICITY_URL = 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity'

type FhirExtension = {
  url?: string
  extension?: FhirExtension[]
  valueCoding?: { code?: string; display?: string }
  valueString?: string
}

function extensionMatchesUrl(ext: FhirExtension, targetUrl: string): boolean {
  const url = (ext.url ?? '').trim()
  if (!url) return false
  if (url === targetUrl) return true
  const suffix = targetUrl.split('/').pop() ?? targetUrl
  return url.endsWith(suffix) || url.includes(suffix)
}

function nestedExtensionValue(ext: FhirExtension, nestedUrl: string): unknown {
  for (const nested of ext.extension ?? []) {
    if ((nested.url ?? '').trim() === nestedUrl) {
      return nested.valueCoding ?? nested.valueString
    }
  }
  return undefined
}

function codingDisplay(coding: unknown): string | null {
  if (!coding || typeof coding !== 'object') return null
  const c = coding as { display?: string; code?: string }
  const display = c.display?.trim()
  if (display) return display
  const code = c.code?.trim()
  return code || null
}

function findUsCoreExtension(patient: Record<string, unknown>, targetUrl: string): FhirExtension | null {
  const extensions = patient.extension
  if (!Array.isArray(extensions)) return null
  for (const ext of extensions) {
    if (ext && typeof ext === 'object' && extensionMatchesUrl(ext as FhirExtension, targetUrl)) {
      return ext as FhirExtension
    }
  }
  return null
}

function ombCategoryFromExtension(ext: FhirExtension): { code?: string; display?: string } | null {
  const value = nestedExtensionValue(ext, 'ombCategory')
  if (value && typeof value === 'object') {
    return value as { code?: string; display?: string }
  }
  return null
}

function textFromExtension(ext: FhirExtension): string | null {
  const value = nestedExtensionValue(ext, 'text')
  if (typeof value === 'string' && value.trim()) return value.trim()
  if (value && typeof value === 'object') return codingDisplay(value)
  return null
}

function displayFromExtension(ext: FhirExtension): string | null {
  const omb = ombCategoryFromExtension(ext)
  let display = omb ? codingDisplay(omb) : null
  if (!display) display = textFromExtension(ext)
  return display?.trim() || null
}

export interface PatientRaceEthnicityFields {
  /** US Core race display text for IMDB ``demographic.race``. */
  race: string | null
  /** US Core ethnicity display text for IMDB ``demographic.ethnicity``. */
  ethnicity: string | null
}

/** Parse US Core race/ethnicity extensions from a FHIR R4 Patient resource. */
export function raceEthnicityFromFhirPatient(patient: Record<string, unknown>): PatientRaceEthnicityFields {
  const raceExt = findUsCoreExtension(patient, US_CORE_RACE_URL)
  const ethnicityExt = findUsCoreExtension(patient, US_CORE_ETHNICITY_URL)

  return {
    race: raceExt ? displayFromExtension(raceExt) : null,
    ethnicity: ethnicityExt ? displayFromExtension(ethnicityExt) : null,
  }
}
